import { useId, useMemo, useState, type ReactNode } from "react";
import type { RoleState } from "../../state/identity.tsx";
import { useDialogFocus } from "../../components/index.ts";
import { WriteButton, canWrite } from "./WriteControls.tsx";
import {
  useApproveAuditKey,
  useRegisterSourceKey,
  useRevokeSource,
} from "./use-sources.ts";
import {
  reverificationStateLabel,
  validateKid,
  validatePublicJwk,
  validateReason,
  writeErrorMessage,
  type AuditKeyPin,
  type PublicJwkValidation,
  type Reverification,
  type Source,
  type SourceKeyInput,
  type TelemetryKey,
} from "./vocab.ts";

/**
 * The WRITE forms and confirmable dialogs the Sources screen shares (task 11.14; design 11 Sources
 * row, design 13, 12.4). Each is admin-gated through {@link WriteButton} / {@link canWrite} and
 * surfaces the server's 400/403/422 verbatim; the enrol wizard reuses the same JWK field and key
 * form so the never-see-private-keys invariant has ONE implementation.
 *
 * The invariant, restated where it is enforced ({@link PublicJwkField}, {@link RegisterTelemetryKeyForm}):
 * the control plane never generates or sees a private key (FR-11.1). These forms:
 *   - offer NO "generate a key pair" affordance — the key pair is made in the component's
 *     environment, not here;
 *   - have NO private-key input — the only key field is the PUBLIC JWK;
 *   - validate the pasted JWK with {@link validatePublicJwk}, which REFUSES any JWK carrying a
 *     private member and never forwards it;
 *   - say so in copy, so the admin knows to paste only the public JWK.
 */

/* ---- The public-JWK field (the heart of the invariant) -------------------------------------- */

export interface PublicJwkFieldProps {
  readonly value: string;
  readonly onChange: (next: string) => void;
  /** The live validation of the current text, so the parent can gate submit on it. */
  readonly validation: PublicJwkValidation;
  /** Show the field's errors only once the admin has typed something (or on submit). */
  readonly showError: boolean;
}

/**
 * A labelled textarea for pasting a PUBLIC telemetry JWK. It is the ONLY key input anywhere in the
 * screen — there is deliberately no file picker for a private key and no "generate" button. The copy
 * tells the admin to generate the key pair in the component's environment and paste only the public
 * JWK. When the pasted text looks like a PRIVATE key, the error is the strongest refusal and the
 * value is never sent (the parent's submit stays disabled because `validation.ok` is false).
 */
export function PublicJwkField(props: PublicJwkFieldProps): ReactNode {
  const { validation } = props;
  const showError = props.showError && !validation.ok && props.value.trim().length > 0;
  return (
    <label className="src-field">
      <span className="src-field__label">Public telemetry JWK (paste JSON, public members only)</span>
      <textarea
        className="src-input src-input--mono"
        value={props.value}
        onChange={(e) => props.onChange(e.target.value)}
        rows={6}
        spellCheck={false}
        autoComplete="off"
        placeholder={'{\n  "kty": "OKP",\n  "crv": "Ed25519",\n  "x": "…",\n  "kid": "…",\n  "use": "sig"\n}'}
        aria-invalid={showError}
      />
      <span className="src-field__hint src-faint">
        Generate the key pair <strong>in the component’s environment</strong>. The control plane only
        ever registers the <strong>public</strong> JWK. It never generates, holds, or accepts a private
        key. Paste the public members (for example <code>kty</code>/<code>crv</code>/<code>x</code>, or{" "}
        <code>kty</code>/<code>n</code>/<code>e</code>).
      </span>
      {showError && (
        <span
          className={validation.privateKeyRejected ? "src-field__error src-field__error--loud" : "src-field__error"}
          role="alert"
        >
          {validation.privateKeyRejected ? "Private key refused. " : ""}
          {validation.message}
        </span>
      )}
    </label>
  );
}

/* ---- Register a telemetry key (rotation) ---------------------------------------------------- */

export interface RegisterTelemetryKeyFormProps {
  readonly role: RoleState;
  readonly source: Source;
  readonly onClose: () => void;
}

/**
 * Register a new PUBLIC telemetry key for a source (`POST /sources/{id}/keys`, `purpose: telemetry`).
 * Supports rotation (design 13): optionally mark an existing key `retiring` until a `not_after`, so
 * both keys are accepted during the overlap and the old one is rejected afterwards. The only key
 * material is the public JWK; a private member is refused by {@link validatePublicJwk}.
 */
export function RegisterTelemetryKeyForm(props: RegisterTelemetryKeyFormProps): ReactNode {
  const titleId = useId();
  const mutation = useRegisterSourceKey();

  const [kid, setKid] = useState("");
  const [jwkText, setJwkText] = useState("");
  const [rotate, setRotate] = useState(false);
  const [retireKid, setRetireKid] = useState("");
  const [retireAt, setRetireAt] = useState(toLocalInput(Date.now() + 24 * 60 * 60 * 1000));
  const [submitted, setSubmitted] = useState(false);

  const jwkValidation = useMemo(() => validatePublicJwk(jwkText), [jwkText]);
  const kidError = validateKid(kid);
  const retireKidError = rotate ? validateKid(retireKid) : null;
  const retireAtMs = fromLocalInput(retireAt);
  const retireAtError = rotate && !Number.isFinite(retireAtMs) ? "Enter a valid retire-by time." : null;

  const activeKids = props.source.telemetry_keys
    .filter((k) => k.status !== "revoked")
    .map((k) => k.kid);

  const canSubmit =
    canWrite(props.role) &&
    jwkValidation.ok &&
    kidError === null &&
    retireKidError === null &&
    retireAtError === null &&
    !mutation.isPending;

  const submit = (): void => {
    setSubmitted(true);
    if (!canSubmit || jwkValidation.jwk === undefined) return;
    const body: SourceKeyInput = {
      purpose: "telemetry",
      kid: kid.trim(),
      public_jwk: jwkValidation.jwk,
    };
    if (rotate) {
      body.retire = { kid: retireKid.trim(), not_after: retireAtMs };
    }
    mutation.mutate({ id: props.source.id, body }, { onSuccess: () => props.onClose() });
  };

  return (
    <Dialog titleId={titleId} title={`Register a public telemetry key for ${props.source.name}`} wide onClose={props.onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="src-field">
          <span className="src-field__label">Key id (`kid`)</span>
          <input
            className="src-input"
            value={kid}
            onChange={(e) => setKid(e.target.value)}
            placeholder="e.g. 2025-06-key"
            aria-invalid={submitted && kidError !== null}
          />
          {submitted && kidError !== null && (
            <span className="src-field__error" role="alert">
              {kidError}
            </span>
          )}
        </label>

        <PublicJwkField value={jwkText} onChange={setJwkText} validation={jwkValidation} showError={submitted} />

        <fieldset className="src-fieldset">
          <legend>Rotation (optional)</legend>
          <label className="src-field src-field--checkbox">
            <input type="checkbox" checked={rotate} onChange={(e) => setRotate(e.target.checked)} />
            <span className="src-field__label">Retire a current key once this one is active</span>
          </label>
          {rotate && (
            <>
              <label className="src-field">
                <span className="src-field__label">Key id to retire</span>
                {activeKids.length > 0 ? (
                  <select
                    className="src-input"
                    value={retireKid}
                    onChange={(e) => setRetireKid(e.target.value)}
                    aria-invalid={submitted && retireKidError !== null}
                  >
                    <option value="">(choose a key)</option>
                    {activeKids.map((k) => (
                      <option key={k} value={k}>
                        {k}
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    className="src-input"
                    value={retireKid}
                    onChange={(e) => setRetireKid(e.target.value)}
                    placeholder="kid of the key being replaced"
                    aria-invalid={submitted && retireKidError !== null}
                  />
                )}
                {submitted && retireKidError !== null && (
                  <span className="src-field__error" role="alert">
                    {retireKidError}
                  </span>
                )}
              </label>
              <label className="src-field">
                <span className="src-field__label">Accept the retiring key until</span>
                <input
                  className="src-input"
                  type="datetime-local"
                  value={retireAt}
                  onChange={(e) => setRetireAt(e.target.value)}
                  aria-invalid={submitted && retireAtError !== null}
                />
                <span className="src-field__hint src-faint">
                  Both keys are accepted until this time; afterwards the retiring key is rejected. Revoke
                  it when you are sure the component has fully rotated.
                </span>
                {submitted && retireAtError !== null && (
                  <span className="src-field__error" role="alert">
                    {retireAtError}
                  </span>
                )}
              </label>
            </>
          )}
        </fieldset>

        {mutation.isError && (
          <p className="src-field__error" role="alert">
            {writeErrorMessage(mutation.error, "key registration")}
          </p>
        )}

        <DialogActions role={props.role} onClose={props.onClose} canSubmit={canSubmit} pending={mutation.isPending} submit={submit}>
          {mutation.isPending ? "Registering…" : "Register public key"}
        </DialogActions>
      </form>
    </Dialog>
  );
}

/* ---- Revoke (confirmable, destructive) ------------------------------------------------------ */

export interface RevokeDialogProps {
  readonly role: RoleState;
  readonly source: Source;
  /** When set, revoke only this telemetry key; otherwise revoke the whole source. */
  readonly telemetryKey?: TelemetryKey | undefined;
  readonly onClose: () => void;
}

/**
 * The confirmable, destructive revoke (`POST /sources/{id}/revoke`, admin; design 12.4). A `key`
 * scopes the revocation to one telemetry key (`kid` in the body); without it the whole source is
 * revoked. The reason is required. The copy explains the design-13 propagation guarantee: edges that
 * already know the revocation reject immediately, propagation targets 30 s globally, and a batch that
 * reaches an edge that has not yet learned the revocation is still processed and marked
 * `accepted_during_revocation_propagation`.
 */
export function RevokeDialog(props: RevokeDialogProps): ReactNode {
  const titleId = useId();
  const mutation = useRevokeSource();
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const scopeIsKey = props.telemetryKey !== undefined;
  const reasonError = validateReason(reason);
  const canSubmit = canWrite(props.role) && reasonError === null && !mutation.isPending;

  const submit = (): void => {
    setSubmitted(true);
    if (!canSubmit) return;
    mutation.mutate(
      {
        id: props.source.id,
        body: scopeIsKey ? { kid: props.telemetryKey?.kid, reason: reason.trim() } : { reason: reason.trim() },
      },
      { onSuccess: () => props.onClose() },
    );
  };

  return (
    <Dialog titleId={titleId} title={scopeIsKey ? "Revoke this telemetry key?" : "Revoke this source?"} alert onClose={props.onClose}>
      <p className="src-dialog__body">
        {scopeIsKey ? (
          <>
            This revokes key <code>{props.telemetryKey?.kid}</code> for <strong>{props.source.name}</strong>. Batches
            signed with it are rejected at every edge that knows the revocation.
          </>
        ) : (
          <>
            This revokes <strong>{props.source.name}</strong> entirely. New batches from it are rejected at
            every edge that knows the revocation.
          </>
        )}
      </p>
      <p className="src-dialog__hint src-faint">
        Edges that already know the revocation reject immediately; global propagation targets 30 seconds.
        A batch that reaches an edge which has not yet learned the revocation is still processed and marked{" "}
        <code>accepted_during_revocation_propagation</code>. Look for that data-quality flag on the
        affected audit chains afterwards. The revocation is recorded in the audit log and cannot be undone.
      </p>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
      >
        <label className="src-field">
          <span className="src-field__label">Reason for revocation (required)</span>
          <textarea
            className="src-input"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={2}
            maxLength={256}
            required
            aria-invalid={submitted && reasonError !== null}
          />
          {submitted && reasonError !== null && (
            <span className="src-field__error" role="alert">
              {reasonError}
            </span>
          )}
        </label>
        {mutation.isError && (
          <p className="src-field__error" role="alert">
            {writeErrorMessage(mutation.error, "revocation")}
          </p>
        )}
        <DialogActions
          role={props.role}
          onClose={props.onClose}
          canSubmit={canSubmit}
          pending={mutation.isPending}
          submit={submit}
          variant="danger"
        >
          {mutation.isPending ? "Revoking…" : scopeIsKey ? "Revoke key" : "Revoke source"}
        </DialogActions>
      </form>
    </Dialog>
  );
}

/* ---- Approve an observed audit key (triggers retroactive re-verification) ------------------- */

export interface ApproveAuditKeyDialogProps {
  readonly role: RoleState;
  readonly source: Source;
  readonly pin: AuditKeyPin;
  readonly onClose: () => void;
}

/**
 * Pin (approve) an observed, unpinned audit signing key (`POST /audit-keys/{id}/approve`, admin). The
 * copy makes clear this triggers RETROACTIVE re-verification of every stored batch of the chain
 * signed by the key (FR-6.6 / design 10.4): valid batches are upgraded to their verified level,
 * invalid batches keep their exact failure. The result's `reverification` reports the queued job.
 */
export function ApproveAuditKeyDialog(props: ApproveAuditKeyDialogProps): ReactNode {
  const titleId = useId();
  const mutation = useApproveAuditKey();
  const [reason, setReason] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const reasonError = validateReason(reason);
  const canSubmit = canWrite(props.role) && reasonError === null && !mutation.isPending;

  const submit = (): void => {
    setSubmitted(true);
    if (!canSubmit) return;
    mutation.mutate(
      { id: props.pin.id, body: { reason: reason.trim() } },
      // Keep the dialog open on success so the admin sees the queued re-verification confirmation.
      {},
    );
  };

  const queued = mutation.data?.reverification;

  return (
    <Dialog titleId={titleId} title="Pin this audit signing key?" onClose={props.onClose}>
      <p className="src-dialog__body">
        Pinning <code>{props.pin.kid}</code> trusts it for verifying this source’s audit chain. The server
        then <strong>re-verifies every stored batch</strong> of the chain that was signed by this key:
        valid batches are upgraded to their verified level, and invalid batches keep their exact failure
        status. Nothing is deleted or rewritten.
      </p>
      {queued === undefined ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit();
          }}
        >
          <label className="src-field">
            <span className="src-field__label">Reason for pinning (required)</span>
            <textarea
              className="src-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={2}
              maxLength={256}
              required
              aria-invalid={submitted && reasonError !== null}
            />
            {submitted && reasonError !== null && (
              <span className="src-field__error" role="alert">
                {reasonError}
              </span>
            )}
          </label>
          {mutation.isError && (
            <p className="src-field__error" role="alert">
              {writeErrorMessage(mutation.error, "audit-key approval")}
            </p>
          )}
          <DialogActions role={props.role} onClose={props.onClose} canSubmit={canSubmit} pending={mutation.isPending} submit={submit} variant="primary">
            {mutation.isPending ? "Pinning…" : "Pin and re-verify"}
          </DialogActions>
        </form>
      ) : (
        <>
          <ReverificationNote reverification={queued} />
          <div className="src-dialog__actions">
            <button type="button" className="src-btn" data-variant="primary" onClick={props.onClose}>
              Done
            </button>
          </div>
        </>
      )}
    </Dialog>
  );
}

/**
 * A compact, honest summary of a re-verification job, shown after an approval and in the pins list.
 * It never claims completion it has not seen: a queued/running job says so, and the upgraded /
 * retained-failure counts are shown only once the server reports them.
 */
export function ReverificationNote(props: { reverification: Reverification }): ReactNode {
  const r = props.reverification;
  const done = r.state === "completed";
  return (
    <p className="src-reverify" data-state={r.state} role="status">
      <strong>{reverificationStateLabel(r.state)}.</strong>{" "}
      {r.chains.toLocaleString()} chain{r.chains === 1 ? "" : "s"}, {r.batches_total.toLocaleString()} batch
      {r.batches_total === 1 ? "" : "es"}
      {done ? (
        <>
          {" "}· {r.batches_upgraded.toLocaleString()} upgraded, {r.batches_retained_failure.toLocaleString()} kept
          their failure.
        </>
      ) : (
        <> queued for re-verification.</>
      )}
    </p>
  );
}

/* ---- Shared dialog shell -------------------------------------------------------------------- */

export function Dialog(props: {
  titleId: string;
  title: string;
  children: ReactNode;
  onClose: () => void;
  wide?: boolean;
  alert?: boolean;
}): ReactNode {
  // Trap/restore focus and wire Escape-to-close (design 12.4). One hook covers every sources dialog.
  const dialogRef = useDialogFocus<HTMLDivElement>(props.onClose);
  return (
    <div className="src-dialog-backdrop" role="presentation" onClick={props.onClose}>
      <div
        ref={dialogRef}
        className={props.wide === true ? "src-dialog src-dialog--wide" : "src-dialog"}
        role={props.alert === true ? "alertdialog" : "dialog"}
        aria-modal="true"
        aria-labelledby={props.titleId}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id={props.titleId} className="src-dialog__title">
          {props.title}
        </h3>
        {props.children}
      </div>
    </div>
  );
}

function DialogActions(props: {
  role: RoleState;
  onClose: () => void;
  canSubmit: boolean;
  pending: boolean;
  submit: () => void;
  variant?: "primary" | "danger";
  children: ReactNode;
}): ReactNode {
  return (
    <div className="src-dialog__actions">
      <WriteButton
        role={props.role}
        type="submit"
        variant={props.variant ?? "primary"}
        disabled={!props.canSubmit}
        onClick={props.submit}
      >
        {props.children}
      </WriteButton>
      <button type="button" className="src-btn" data-variant="ghost" onClick={props.onClose}>
        Cancel
      </button>
    </div>
  );
}

/* ---- datetime-local helpers ----------------------------------------------------------------- */

/** Format an epoch-ms into the local value a `datetime-local` input expects (no timezone). */
export function toLocalInput(ms: number): string {
  const d = new Date(ms);
  const pad = (n: number): string => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Parse a `datetime-local` value back to epoch-ms, or NaN when empty/invalid. */
export function fromLocalInput(value: string): number {
  if (value.trim().length === 0) return Number.NaN;
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : Number.NaN;
}
